// Lightweight spam-protection helpers used by public submission endpoints.
// - Honeypot: hidden field that humans never fill but bots do.
// - Min-fill-time: forms submitted in <2s are almost always bots.
// - IP rate-limit: per-worker in-memory rolling window. Resets on cold start
//   but blunts trivial floods. Acceptable per the project's documented
//   no-standard-rate-limiting tradeoff.

export const HONEYPOT_FIELD = "company_website";
export const MIN_FILL_MS = 1500;

export function checkHoneypot(value: unknown): { ok: true } | { ok: false; reason: string } {
  if (typeof value === "string" && value.trim().length > 0) {
    return { ok: false, reason: "spam_honeypot" };
  }
  return { ok: true };
}

export function checkFillTime(startedAt: unknown): { ok: true } | { ok: false; reason: string } {
  const t = typeof startedAt === "number" ? startedAt : Number(startedAt);
  if (!Number.isFinite(t) || t <= 0) return { ok: true }; // tolerate missing
  const dt = Date.now() - t;
  if (dt < MIN_FILL_MS) return { ok: false, reason: "spam_too_fast" };
  if (dt > 1000 * 60 * 60 * 6) return { ok: false, reason: "form_stale" };
  return { ok: true };
}

type Bucket = { count: number; resetAt: number };
const buckets = new Map<string, Bucket>();

export function rateLimit(
  key: string,
  { limit, windowMs }: { limit: number; windowMs: number },
): { ok: true; remaining: number } | { ok: false; retryAfterSec: number } {
  const now = Date.now();
  const b = buckets.get(key);
  if (!b || b.resetAt <= now) {
    buckets.set(key, { count: 1, resetAt: now + windowMs });
    return { ok: true, remaining: limit - 1 };
  }
  if (b.count >= limit) {
    return { ok: false, retryAfterSec: Math.ceil((b.resetAt - now) / 1000) };
  }
  b.count += 1;
  return { ok: true, remaining: limit - b.count };
}
